import { useMutation, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { apiFetch } from '@shared/api/http'
import {
  RecuperaOptOutSchema,
  RecuperaRecoveryProductsSchema,
  RecuperaStatusSchema,
} from './recupera-ai.contract'
import type {
  RecuperaOptOut,
  RecuperaRecoveryProducts,
  RecuperaStatus,
} from './recupera-ai.contract'
import { recuperaAiKeys } from './recupera-ai.queries'

export function useRecuperaOptInMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (): Promise<RecuperaStatus> =>
      RecuperaStatusSchema.parse(await apiFetch<unknown>('/recupera/opt-in', { method: 'POST' })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['recupera-ai'] })
    },
  })
}

export function useSetRecoveryProductsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (productIds: readonly string[]): Promise<RecuperaRecoveryProducts> =>
      RecuperaRecoveryProductsSchema.parse(
        await apiFetch<unknown>('/recupera/recovery-products', {
          method: 'PUT',
          body: { productIds },
        }),
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(recuperaAiKeys.recoveryProducts(), data)
      void queryClient.invalidateQueries({ queryKey: ['recupera-ai'] })
    },
  })
}

export function useSetRecuperaProductActiveMutation(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (active: boolean): Promise<{ active: boolean }> =>
      z.object({ active: z.boolean() }).parse(
        await apiFetch<unknown>(`/recupera/products/${productId}/active`, {
          method: 'PUT',
          body: { active },
        }),
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(recuperaAiKeys.productActive(productId), data)
      void queryClient.invalidateQueries({ queryKey: ['recupera-ai'] })
    },
  })
}

export function useRecuperaOptOutMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (): Promise<RecuperaOptOut> =>
      RecuperaOptOutSchema.parse(
        await apiFetch<unknown>('/recupera/opt-out', { method: 'DELETE' }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['recupera-ai'] })
    },
  })
}
